import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  Chip,
  LinearProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  AccountTree as WorkflowIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';

interface WorkflowItem {
  id: string;
  name: string;
  status: 'running' | 'paused' | 'completed' | 'error' | 'pending';
  progress: number;
  steps: WorkflowStep[];
  createdAt: string;
  lastUpdated: string;
}

interface WorkflowStep {
  id: string;
  name: string;
  status: 'completed' | 'active' | 'pending' | 'error';
  description: string;
}

const Workflow: React.FC = () => {
  const [workflows, setWorkflows] = useState<WorkflowItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowItem | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Simulated data
  const mockWorkflows: WorkflowItem[] = [
    {
      id: '1',
      name: 'SAP Data Synchronization',
      status: 'running',
      progress: 65,
      steps: [
        { id: '1', name: 'Connection Test', status: 'completed', description: 'Verify SAP connection' },
        { id: '2', name: 'Data Extraction', status: 'active', description: 'Extract data from SAP system' },
        { id: '3', name: 'Data Validation', status: 'pending', description: 'Validate data integrity' },
        { id: '4', name: 'Data Import', status: 'pending', description: 'Import data to target system' },
      ],
      createdAt: '2024-01-15 09:00:00',
      lastUpdated: '2024-01-15 10:30:00',
    },
    {
      id: '2',
      name: 'SAP User Provisioning',
      status: 'completed',
      progress: 100,
      steps: [
        { id: '1', name: 'User Validation', status: 'completed', description: 'Validate user information' },
        { id: '2', name: 'Role Assignment', status: 'completed', description: 'Assign roles and permissions' },
        { id: '3', name: 'Account Creation', status: 'completed', description: 'Create SAP account' },
        { id: '4', name: 'Notification', status: 'completed', description: 'Send notification to user' },
      ],
      createdAt: '2024-01-14 14:00:00',
      lastUpdated: '2024-01-14 15:30:00',
    },
    {
      id: '3',
      name: 'SAP Backup Process',
      status: 'paused',
      progress: 30,
      steps: [
        { id: '1', name: 'Pre-backup Check', status: 'completed', description: 'Check available space' },
        { id: '2', name: 'Database Backup', status: 'active', description: 'Create database backup' },
        { id: '3', name: 'File Backup', status: 'pending', description: 'Backup system files' },
        { id: '4', name: 'Verification', status: 'pending', description: 'Verify backup integrity' },
      ],
      createdAt: '2024-01-15 08:00:00',
      lastUpdated: '2024-01-15 09:15:00',
    },
  ];

  useEffect(() => {
    fetchWorkflows();
  }, []);

  const fetchWorkflows = async () => {
    setLoading(true);
    setError(null);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setWorkflows(mockWorkflows);
    } catch (err) {
      setError('Error loading workflows. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'running': return 'primary';
      case 'completed': return 'success';
      case 'paused': return 'warning';
      case 'error': return 'error';
      case 'pending': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'running': return <PlayIcon />;
      case 'completed': return <CheckCircleIcon />;
      case 'paused': return <PauseIcon />;
      case 'error': return <ErrorIcon />;
      case 'pending': return <ScheduleIcon />;
      default: return <ScheduleIcon />;
    }
  };

  const handleWorkflowAction = (workflowId: string, action: string) => {
    // Implement workflow actions
    console.log(`Action ${action} on workflow ${workflowId}`);
  };

  const handleViewDetails = (workflow: WorkflowItem) => {
    setSelectedWorkflow(workflow);
    setDialogOpen(true);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Workflow Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage and monitor SAP system workflows
        </Typography>
      </Box>

      {/* Actions */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" color="text.primary">
            Active Workflows: {workflows.filter(w => w.status === 'running').length}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => console.log('Create new workflow')}
          >
            New Workflow
          </Button>
        </Box>
      </Paper>

      {/* Content */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && (
          <Grid container spacing={2}>
            {workflows.map((workflow) => (
              <Grid item xs={12} md={6} lg={4} key={workflow.id}>
                <Card 
                  sx={{ 
                    height: '100%',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 3,
                    }
                  }}
                >
                  <CardHeader
                    avatar={<WorkflowIcon color="primary" />}
                    title={workflow.name}
                    subheader={`Created: ${workflow.createdAt}`}
                    titleTypographyProps={{ fontSize: '1rem', fontWeight: 'bold' }}
                    subheaderTypographyProps={{ fontSize: '0.8rem' }}
                  />
                  <CardContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {/* Status and Progress */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Chip
                          icon={getStatusIcon(workflow.status)}
                          label={workflow.status.toUpperCase()}
                          color={getStatusColor(workflow.status) as any}
                          size="small"
                        />
                        <Typography variant="body2" color="text.secondary">
                          {workflow.progress}%
                        </Typography>
                      </Box>
                      
                      <LinearProgress 
                        variant="determinate" 
                        value={workflow.progress}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                      
                      {/* Last Updated */}
                      <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                        Last updated: {workflow.lastUpdated}
                      </Typography>
                      
                      {/* Actions */}
                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          fullWidth
                          onClick={() => handleViewDetails(workflow)}
                        >
                          View Details
                        </Button>
                        {workflow.status === 'running' && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            onClick={() => handleWorkflowAction(workflow.id, 'pause')}
                          >
                            <PauseIcon fontSize="small" />
                          </Button>
                        )}
                        {workflow.status === 'paused' && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            onClick={() => handleWorkflowAction(workflow.id, 'resume')}
                          >
                            <PlayIcon fontSize="small" />
                          </Button>
                        )}
                        {(workflow.status === 'running' || workflow.status === 'paused') && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => handleWorkflowAction(workflow.id, 'stop')}
                          >
                            <StopIcon fontSize="small" />
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {!loading && !error && workflows.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              No workflows available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create a new workflow to get started
            </Typography>
          </Box>
        )}
      </Box>

      {/* Workflow Details Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Workflow Details: {selectedWorkflow?.name}
        </DialogTitle>
        <DialogContent>
          {selectedWorkflow && (
            <Box sx={{ mt: 2 }}>
              <Stepper orientation="vertical">
                {selectedWorkflow.steps.map((step) => (
                  <Step key={step.id} active={step.status === 'active'} completed={step.status === 'completed'}>
                    <StepLabel
                      error={step.status === 'error'}
                      icon={
                        step.status === 'completed' ? <CheckCircleIcon color="success" /> :
                        step.status === 'error' ? <ErrorIcon color="error" /> :
                        step.status === 'active' ? <PlayIcon color="primary" /> :
                        <ScheduleIcon color="disabled" />
                      }
                    >
                      {step.name}
                    </StepLabel>
                    <StepContent>
                      <Typography variant="body2" color="text.secondary">
                        {step.description}
                      </Typography>
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Workflow; 